/**
 * SegurancaSection — Profile tab for account security settings.
 *
 * Currently exposes the optional Two-Factor Authentication (2FA) flow:
 *   - If OFF: user clicks "Ativar", enters password -> backend sends OTP,
 *     user enters 6-digit code -> 2FA flips on.
 *   - If ON: user clicks "Desativar", enters password -> 2FA flips off.
 *
 * Endpoints:
 *   POST /api/auth/2fa/enable          { password }   -> sends OTP
 *   POST /api/auth/2fa/confirm-enable  { codigo }     -> activates
 *   POST /api/auth/2fa/disable         { password }   -> deactivates
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Loader2, Lock, KeyRound, CheckCircle2, X, Mail, Smartphone } from 'lucide-react';
import { api } from '../services/api';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{label}</span>
    <input
      {...props}
      className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all bg-white"
    />
  </label>
);

export default function SegurancaSection({ user, refreshUser }) {
  const is2FAOn = !!user?.two_factor_enabled;
  const currentChannel = user?.two_factor_channel || 'email';
  const hasPhone = !!user?.telefone;

  // "enable" flow: password -> OTP sent -> code confirm
  const [enableOpen, setEnableOpen] = useState(false);
  const [enableStep, setEnableStep] = useState(1); // 1 = password, 2 = otp
  const [enableChannel, setEnableChannel] = useState('email'); // 'email' | 'sms'
  const [enablePassword, setEnablePassword] = useState('');
  const [enableOtp, setEnableOtp] = useState('');
  const [enableMaskedEmail, setEnableMaskedEmail] = useState('');
  const [enableMaskedPhone, setEnableMaskedPhone] = useState('');
  const [enableLoading, setEnableLoading] = useState(false);
  const [enableError, setEnableError] = useState('');

  // "disable" flow: just password
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState('');

  const resetEnable = () => {
    setEnableOpen(false);
    setEnableStep(1);
    setEnableChannel('email');
    setEnablePassword('');
    setEnableOtp('');
    setEnableMaskedEmail('');
    setEnableMaskedPhone('');
    setEnableError('');
  };
  const resetDisable = () => {
    setDisableOpen(false);
    setDisablePassword('');
    setDisableError('');
  };

  const handleEnablePassword = async (e) => {
    e?.preventDefault?.();
    if (!enablePassword) {
      setEnableError('Informe sua senha atual.');
      return;
    }
    if (enableChannel === 'sms' && !hasPhone) {
      setEnableError('Adicione um telefone ao perfil antes de usar 2FA por SMS.');
      return;
    }
    setEnableLoading(true);
    setEnableError('');
    try {
      const { data } = await api.post('/auth/2fa/enable', {
        password: enablePassword,
        channel: enableChannel,
      });
      setEnableMaskedEmail(data?.email_masked || user?.email || '');
      setEnableMaskedPhone(data?.phone_masked || '');
      setEnableStep(2);
      toast.success(
        enableChannel === 'sms'
          ? 'Código enviado por SMS.'
          : 'Código enviado para seu e-mail.'
      );
    } catch (err) {
      setEnableError(err?.response?.data?.detail || 'Não foi possível iniciar a ativação.');
    } finally {
      setEnableLoading(false);
    }
  };

  const handleEnableConfirm = async (codigoOverride) => {
    const codigo = (codigoOverride ?? enableOtp).trim();
    if (codigo.length !== 6) {
      setEnableError('Digite os 6 dígitos do código.');
      return;
    }
    setEnableLoading(true);
    setEnableError('');
    try {
      await api.post('/auth/2fa/confirm-enable', { codigo });
      toast.success('Verificação em duas etapas ativada.');
      await refreshUser?.();
      resetEnable();
    } catch (err) {
      setEnableError(err?.response?.data?.detail || 'Código inválido ou expirado.');
      setEnableOtp('');
    } finally {
      setEnableLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e?.preventDefault?.();
    if (!disablePassword) {
      setDisableError('Informe sua senha atual.');
      return;
    }
    setDisableLoading(true);
    setDisableError('');
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword });
      toast.success('Verificação em duas etapas desativada.');
      await refreshUser?.();
      resetDisable();
    } catch (err) {
      setDisableError(err?.response?.data?.detail || 'Não foi possível desativar.');
    } finally {
      setDisableLoading(false);
    }
  };

  // Auto-submit when OTP reaches 6 digits
  React.useEffect(() => {
    if (enableStep === 2 && enableOtp.length === 6 && !enableLoading) {
      handleEnableConfirm(enableOtp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableOtp, enableStep]);

  return (
    <div className="space-y-6" data-testid="seguranca-section">
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Segurança da conta</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Proteja o acesso à sua conta com uma camada extra de verificação.
        </p>
      </div>

      {/* 2FA Card */}
      <div className="bg-white rounded-2xl border border-[var(--border)] p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex gap-4 flex-1 min-w-[240px]">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${is2FAOn ? 'bg-emerald-50' : 'bg-purple-50'}`}>
              {is2FAOn ? (
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
              ) : (
                <ShieldOff className="w-6 h-6 text-purple-600" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-base" style={{ fontFamily: 'Manrope' }}>
                  Verificação em duas etapas
                </h3>
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    is2FAOn ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                  }`}
                  data-testid="two-fa-status-badge"
                >
                  {is2FAOn ? 'Ativo' : 'Inativo'}
                </span>
                {is2FAOn && (
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100 inline-flex items-center gap-1"
                    data-testid="two-fa-channel-badge"
                  >
                    {currentChannel === 'sms' ? <Smartphone className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                    {currentChannel === 'sms' ? 'por SMS' : 'por e-mail'}
                  </span>
                )}
              </div>
              <p className="text-sm text-[var(--text-secondary)] mt-1 leading-relaxed">
                {is2FAOn
                  ? (currentChannel === 'sms'
                      ? 'A cada login, enviaremos um SMS com código de 6 dígitos antes de liberar o acesso.'
                      : 'A cada login, enviaremos um código de 6 dígitos para seu e-mail antes de liberar o acesso.')
                  : 'A ativação da verificação em duas etapas está temporariamente indisponível.'}
              </p>
            </div>
          </div>

          {is2FAOn ? (
            <button
              type="button"
              onClick={() => setDisableOpen(true)}
              className="bg-white text-red-600 border border-red-200 hover:bg-red-50 rounded-full px-5 py-2 font-semibold text-sm transition-all active:scale-95 flex items-center gap-2"
              data-testid="two-fa-disable-btn"
            >
              <ShieldOff className="w-4 h-4" /> Desativar
            </button>
          ) : (
            /* Ativação de 2FA temporariamente indisponível para contas de
               usuário (decisão do produto). Quem já ativou ainda pode desativar. */
            <span
              className="text-xs font-medium px-3 py-2 rounded-full bg-gray-100 text-gray-500"
              data-testid="two-fa-unavailable-badge"
            >
              Indisponível no momento
            </span>
          )}
        </div>
      </div>

      {/* Enable Modal */}
      {enableOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={resetEnable}>
          <div
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="two-fa-enable-modal"
          >
            <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[var(--primary)]" />
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
                  Ativar verificação em duas etapas
                </h3>
              </div>
              <button type="button" onClick={resetEnable} className="text-gray-400 hover:text-gray-600 p-1" data-testid="two-fa-enable-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 sm:p-6">
              {enableStep === 1 && (
                <form onSubmit={handleEnablePassword} className="space-y-4">
                  <div className="flex gap-3 items-start p-3 rounded-xl bg-purple-50 border border-purple-100">
                    <Lock className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Para sua segurança, confirme sua senha e escolha como receber os códigos.
                    </p>
                  </div>

                  {/* Channel picker */}
                  <div>
                    <span className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">Receber código por</span>
                    <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Canal 2FA">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={enableChannel === 'email'}
                        onClick={() => setEnableChannel('email')}
                        disabled={enableLoading}
                        className={`flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-all ${
                          enableChannel === 'email'
                            ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-200'
                            : 'border-[var(--border)] hover:border-violet-300'
                        }`}
                        data-testid="two-fa-channel-email"
                      >
                        <div className="flex items-center gap-2 text-sm font-semibold">
                          <Mail className="w-4 h-4 text-violet-600" /> E-mail
                        </div>
                        <span className="text-xs text-[var(--text-secondary)]">Código enviado para seu e-mail cadastrado</span>
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={enableChannel === 'sms'}
                        onClick={() => setEnableChannel('sms')}
                        disabled={enableLoading || !hasPhone}
                        className={`flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                          enableChannel === 'sms'
                            ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-200'
                            : 'border-[var(--border)] hover:border-violet-300'
                        }`}
                        data-testid="two-fa-channel-sms"
                      >
                        <div className="flex items-center gap-2 text-sm font-semibold">
                          <Smartphone className="w-4 h-4 text-violet-600" /> SMS
                        </div>
                        <span className="text-xs text-[var(--text-secondary)]">
                          {hasPhone ? 'Código enviado para seu celular' : 'Cadastre um telefone no perfil'}
                        </span>
                      </button>
                    </div>
                  </div>

                  <Field
                    type="password"
                    label="Senha atual"
                    placeholder="Digite sua senha"
                    value={enablePassword}
                    onChange={(e) => { setEnablePassword(e.target.value); if (enableError) setEnableError(''); }}
                    disabled={enableLoading}
                    autoFocus
                    data-testid="two-fa-enable-password"
                  />
                  {enableError && (
                    <p className="text-sm text-red-600" data-testid="two-fa-enable-error">{enableError}</p>
                  )}
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={resetEnable}
                      className="flex-1 bg-white text-gray-700 border border-[var(--border)] hover:bg-gray-50 rounded-full px-5 py-2.5 font-semibold text-sm transition-all"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={enableLoading || !enablePassword}
                      className="flex-1 bg-[var(--primary)] text-white hover:bg-[#450066] rounded-full px-5 py-2.5 font-semibold text-sm transition-all shadow-sm disabled:opacity-60 flex items-center justify-center gap-2"
                      data-testid="two-fa-enable-submit-password"
                    >
                      {enableLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : 'Continuar'}
                    </button>
                  </div>
                </form>
              )}

              {enableStep === 2 && (
                <div className="space-y-4">
                  <div className="flex gap-3 items-start p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                    <KeyRound className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {enableChannel === 'sms' ? (
                        <>Enviamos um SMS com o código de 6 dígitos para <strong>{enableMaskedPhone || 'seu telefone'}</strong>. Ele expira em 10 minutos.</>
                      ) : (
                        <>Enviamos um código de 6 dígitos para <strong>{enableMaskedEmail}</strong>. Ele expira em 10 minutos.</>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col items-center gap-3 py-2">
                    <InputOTP
                      maxLength={6}
                      value={enableOtp}
                      onChange={(v) => { setEnableOtp(v); if (enableError) setEnableError(''); }}
                      disabled={enableLoading}
                      autoFocus
                      inputMode="numeric"
                      data-testid="two-fa-enable-otp"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="w-11 h-12 sm:w-12 sm:h-14 text-lg" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                    {enableError && (
                      <p className="text-sm text-red-600 text-center" data-testid="two-fa-enable-otp-error">{enableError}</p>
                    )}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => { setEnableStep(1); setEnableOtp(''); setEnableError(''); }}
                      className="flex-1 bg-white text-gray-700 border border-[var(--border)] hover:bg-gray-50 rounded-full px-5 py-2.5 font-semibold text-sm transition-all"
                      disabled={enableLoading}
                    >
                      Voltar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEnableConfirm()}
                      disabled={enableLoading || enableOtp.length !== 6}
                      className="flex-1 bg-[var(--primary)] text-white hover:bg-[#450066] rounded-full px-5 py-2.5 font-semibold text-sm transition-all shadow-sm disabled:opacity-60 flex items-center justify-center gap-2"
                      data-testid="two-fa-enable-submit-otp"
                    >
                      {enableLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Ativando...</> : <><CheckCircle2 className="w-4 h-4" /> Ativar</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Disable Modal */}
      {disableOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={resetDisable}>
          <div
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="two-fa-disable-modal"
          >
            <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
              <div className="flex items-center gap-2">
                <ShieldOff className="w-5 h-5 text-red-600" />
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
                  Desativar verificação em duas etapas
                </h3>
              </div>
              <button type="button" onClick={resetDisable} className="text-gray-400 hover:text-gray-600 p-1" data-testid="two-fa-disable-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleDisable} className="p-5 sm:p-6 space-y-4">
              <div className="flex gap-3 items-start p-3 rounded-xl bg-red-50 border border-red-100">
                <Lock className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-700 leading-relaxed">
                  Sua conta ficará menos protegida. Confirme sua senha para prosseguir.
                </p>
              </div>
              <Field
                type="password"
                label="Senha atual"
                placeholder="Digite sua senha"
                value={disablePassword}
                onChange={(e) => { setDisablePassword(e.target.value); if (disableError) setDisableError(''); }}
                disabled={disableLoading}
                autoFocus
                data-testid="two-fa-disable-password"
              />
              {disableError && (
                <p className="text-sm text-red-600" data-testid="two-fa-disable-error">{disableError}</p>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={resetDisable}
                  className="flex-1 bg-white text-gray-700 border border-[var(--border)] hover:bg-gray-50 rounded-full px-5 py-2.5 font-semibold text-sm transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={disableLoading || !disablePassword}
                  className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-full px-5 py-2.5 font-semibold text-sm transition-all shadow-sm disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="two-fa-disable-submit"
                >
                  {disableLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Desativando...</> : 'Desativar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
